import { ValueOf } from "type-fest";

/**
 * Gate models a deploy workflow can be held behind.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export const DEPLOY_GATE = {
  /**
   * Hold each deploy job behind a GitHub environment so its protection rules —
   * required reviewers, wait timers, deployment branch policies — apply before
   * the job is sent to a runner.
   */
  ENVIRONMENT: "environment",

  /**
   * Split the workflow in two. A **plan** workflow builds and diffs but never
   * deploys; a dispatch-only **apply** workflow deploys the exact cloud
   * assembly a nominated plan run produced.
   *
   * Needs no protection rule, so unlike {@link DEPLOY_GATE.ENVIRONMENT} it
   * works on every GitHub plan. `environmentName` is optional here and layers
   * an environment onto the apply jobs.
   */
  PLAN_APPLY: "plan-apply",
} as const;

export type DeployGate = ValueOf<typeof DEPLOY_GATE>;

/**
 * How many approvals the {@link DEPLOY_GATE.ENVIRONMENT} gate asks an operator
 * for across one run.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export const DEPLOY_APPROVALS = {
  /**
   * One approval per deploy job. The environment sits on every deploy job, so
   * each reaches its protection rules on its own — and deploy jobs chained by
   * `deployAfterTargets` reach them one at a time, with a full deploy between
   * each prompt.
   */
  PER_TARGET: "per-target",

  /**
   * One approval for the whole run. A dedicated approval job carries the
   * required-reviewer rule and every deploy job waits on it, so the operator
   * approves once and the rest of the run proceeds unattended.
   *
   * Requires `approvalEnvironmentName`, and it must name a *different*
   * environment than the deploy jobs' — the deploy jobs keep their own
   * `environment` so their OIDC subject claims, environment-scoped secrets,
   * and per-stack deployment history are untouched.
   */
  ONCE: "once",
} as const;

export type DeployApprovals = ValueOf<typeof DEPLOY_APPROVALS>;

/**
 * Job id of the single approval job emitted under
 * {@link DEPLOY_APPROVALS.ONCE}.
 */
export const APPROVE_JOB_ID = "approve";

/**
 * Resolve the GitHub environment the single approval job sits behind,
 * validating the pairing at synth time.
 *
 * Mirrors {@link resolveEnvironmentGate}: every rejected combination is one
 * that renders a workflow which *looks* gated the way it was configured and is
 * not. A name with no `approvals: "once"` is inert, `approvals: "once"` with no
 * name has nothing to gate on, `approvals: "once"` outside the `environment`
 * gate has no deploy jobs in this workflow to hold, and an approval
 * environment equal to the deploy environment reintroduces the per-target
 * prompts the option exists to remove.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 * @param gate - Gate model, or `undefined` to leave the workflow ungated.
 * @param approvals - Approval model, defaulting to `per-target` when unset.
 * @param approvalEnvironmentName - Approval environment, required under `once`.
 * @param environmentName - Resolved deploy environment, used for the collision check.
 * @param componentName - Component name used in the thrown error message.
 * @returns The approval environment name, or `undefined` under `per-target`.
 */
export const resolveApprovalGate = (
  gate: DeployGate | undefined,
  approvals: DeployApprovals | undefined,
  approvalEnvironmentName: string | undefined,
  environmentName: string | undefined,
  componentName: string,
): string | undefined => {
  const trimmed = approvalEnvironmentName?.trim();

  if (approvals !== DEPLOY_APPROVALS.ONCE) {
    if (trimmed) {
      throw new Error(
        `${componentName} requires \`approvals\` to be "${DEPLOY_APPROVALS.ONCE}" when \`approvalEnvironmentName\` is supplied, got "${approvalEnvironmentName}" with \`approvals\` ${
          approvals ? `"${approvals}"` : "unset"
        }`,
      );
    }
    return undefined;
  }

  if (gate !== DEPLOY_GATE.ENVIRONMENT) {
    throw new Error(
      `${componentName} requires \`gate\` to be "${DEPLOY_GATE.ENVIRONMENT}" when \`approvals\` is "${DEPLOY_APPROVALS.ONCE}", got ${
        gate ? `"${gate}"` : "no gate"
      }`,
    );
  }

  if (!trimmed) {
    throw new Error(
      `${componentName} requires a non-empty \`approvalEnvironmentName\` when \`approvals\` is "${DEPLOY_APPROVALS.ONCE}"`,
    );
  }

  if (trimmed === environmentName?.trim()) {
    throw new Error(
      `${componentName} requires \`approvalEnvironmentName\` to name a different environment than \`environmentName\`, got "${trimmed}" for both — a shared environment puts the reviewer rule back on every deploy job, which is the per-target prompting "${DEPLOY_APPROVALS.ONCE}" exists to remove`,
    );
  }

  return trimmed;
};

/**
 * Resolve the GitHub environment a workflow's gated jobs sit behind,
 * validating the pairing at synth time.
 *
 * A name with no `gate` throws, because it is silently inert. A `gate` with no
 * name throws only under {@link DEPLOY_GATE.ENVIRONMENT}, where the
 * environment *is* the whole gate and has nothing to attach to without one.
 * Under {@link DEPLOY_GATE.PLAN_APPLY} the dispatch split is the gate and the
 * environment is an optional second checkpoint on the apply jobs.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 * @param gate - Gate model, or `undefined` to leave the workflow ungated.
 * @param environmentName - Environment name, required under `environment`.
 * @param componentName - Component name used in the thrown error message.
 * @returns The environment name, or `undefined` when no environment applies.
 */
export const resolveEnvironmentGate = (
  gate: DeployGate | undefined,
  environmentName: string | undefined,
  componentName: string,
): string | undefined => {
  const trimmed = environmentName?.trim();

  if (gate === undefined) {
    if (trimmed) {
      throw new Error(
        `${componentName} requires \`gate\` to be set when \`environmentName\` is supplied, got "${environmentName}" with no gate`,
      );
    }
    return undefined;
  }

  if (gate === DEPLOY_GATE.ENVIRONMENT && !trimmed) {
    throw new Error(
      `${componentName} requires a non-empty \`environmentName\` when \`gate\` is "${gate}"`,
    );
  }

  return trimmed;
};

/**
 * Resolve whether the per-target `cdk diff` jobs carry the deploy
 * environment, validating the pairing at synth time.
 *
 * The flag exists for one reason: a job's OIDC subject claim is
 * `repo:<owner>/<repo>:environment:<name>` when the job declares an
 * environment and `repo:<owner>/<repo>:ref:refs/heads/<branch>` when it does
 * not. A deployer-role trust policy narrowed to environment subjects alone
 * therefore rejects the diff jobs — the only cloud-touching jobs the
 * {@link DEPLOY_GATE.ENVIRONMENT} gate leaves on the ref claim — and since the
 * merged report gates every deploy, the whole run dies before anything
 * deploys. Stamping the environment on the diff jobs puts them on the same
 * subject as the deploys they inform.
 *
 * Mirrors {@link resolveEnvironmentGate} and {@link resolveApprovalGate}:
 * every rejected combination renders a workflow whose diff jobs *look*
 * environment-scoped and are not, and the symptom is a role-assumption failure
 * at run time rather than an error at synth time.
 *
 * Outside {@link DEPLOY_GATE.ENVIRONMENT} there is no deploy environment to
 * borrow. Under {@link DEPLOY_GATE.PLAN_APPLY} that is deliberate rather than
 * incidental: the deploy jobs live in the apply workflow, so any
 * `environmentName` belongs to *that* workflow, and the plan's diff is the
 * review surface an operator reads before dispatching the apply. Holding it
 * behind the apply's environment would make the review wait on the approval it
 * exists to inform.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 * @param gate - Gate model, or `undefined` to leave the workflow ungated.
 * @param diffEnvironment - The `diff.environment` flag as the caller set it.
 * @param diffEnabled - Resolved `diff.enabled`, so an inert flag is caught.
 * @param componentName - Component name used in the thrown error message.
 * @returns Whether the diff jobs carry the deploy environment.
 */
export const resolveDiffEnvironment = (
  gate: DeployGate | undefined,
  diffEnvironment: boolean | undefined,
  diffEnabled: boolean,
  componentName: string,
): boolean => {
  if (!diffEnvironment) {
    return false;
  }

  if (gate !== DEPLOY_GATE.ENVIRONMENT) {
    throw new Error(
      `${componentName} requires \`gate\` to be "${DEPLOY_GATE.ENVIRONMENT}" when \`diff.environment\` is set, got ${
        gate ? `"${gate}"` : "no gate"
      }`,
    );
  }

  if (!diffEnabled) {
    throw new Error(
      `${componentName} requires \`diff.enabled\` when \`diff.environment\` is set — there are no diff jobs to put the environment on`,
    );
  }

  return true;
};
